import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChild,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MediaChange, MediaObserver } from '@ngbracket/ngx-layout';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { MngUserService } from '@mng/mng-auth';
import { MngBaseSubscriptionComponent } from '@mng/mng-base-subscription-component';
import { UserContextData } from '@mng/domain';
import { MngActionDef } from '@mng/mng-inputs';
import { Store } from '@ngxs/store';
import { getActiveArea, getRightSidenavEnabled, getSidenavActionDefs, IUiActiveArea } from './frame.state';
import { CobrowsingService } from '@mng/mng-cobrowsing';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AsyncPipe, Location, NgTemplateOutlet } from '@angular/common';
import { SidenavRightService } from './sidenav-right.service';
import { removeParams } from './frame.logic';
import { IFrameHandler } from './frame.types';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { MatButton } from '@angular/material/button';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { MngIconComponent } from '@mng/mng-icon';
import { MngSidenavButtonComponent } from './mng-sidenav-button/mng-sidenav-button.component';
import { MatListItem, MatListItemAvatar, MatListItemTitle, MatNavList } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { FlexDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';

export interface ExternalRoute {
  url: string;
  target: '_self' | '_blank';
}

@Component({
  selector: 'mng-frame',
  imports: [
    MatSidenavContainer,
    MatSidenav,
    LayoutDirective,
    FlexDirective,
    MatToolbar,
    MatIcon,
    MatNavList,
    MngSidenavButtonComponent,
    NgTemplateOutlet,
    MatListItem,
    MngIconComponent,
    MatListItemAvatar,
    MatListItemTitle,
    CdkPortalOutlet,
    MatSidenavContent,
    MatToolbarRow,
    MatButton,
    MatTabNav,
    MatTabLink,
    MatTabNavPanel,
    RouterOutlet,
    AsyncPipe
  ],
  templateUrl: './mng-frame.component.html',
  styleUrl: './mng-frame.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MngFrameComponent extends MngBaseSubscriptionComponent implements OnInit, AfterViewInit, OnDestroy {
  media = inject(MediaObserver);
  private store = inject(Store);
  private router = inject(Router);
  private cobrowsingService = inject(CobrowsingService);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private sanitizer = inject(DomSanitizer);
  sidenavRightService = inject(SidenavRightService);
  private cdr = inject(ChangeDetectorRef);
  private readonly userService = inject(MngUserService);

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('sidenavRight', { static: true }) sidenavRight: MatSidenav;
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef<HTMLDivElement>;

  sidenavTemplateRef = contentChild<TemplateRef<any>>('sidenavTemplate');

  sidenavActionDefs: MngActionDef[] = [];

  @Input({ transform: booleanAttribute }) withToolbarTitles = true;

  @Input({ transform: booleanAttribute }) needsAuth = true;

  @Input({ transform: booleanAttribute }) needsSidenav = true;

  @Input() contentWidth = '100%';

  @Input() logoHref = '/';

  @Input() envInfo: string;

  @Input({ transform: booleanAttribute })
  withNavTabs = false;

  @Input()
  frameHandler: IFrameHandler;

  userContextData: UserContextData;

  activeArea: IUiActiveArea;

  posTop = 64;

  currentComponent: any; // Referenz auf die "aktuelle" Route Component

  noFrame = false;

  minimalFrame = false;

  rightSidenavEnabled = false;

  constructor() {
    super();

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params.mobileapp === 'true') {
        this.noFrame = true;
      }
      if (params.minimalframe === 'true') {
        this.minimalFrame = true;
      }
    });
  }

  startCobrowsing() {
    this.cobrowsingService.startCobrowsing();
  }

  get cobrowsingButtonVisible(): boolean {
    return this.cobrowsingService.showStartButton;
  }

  handleState(state) {
    if (this.needsAuth) {
      this.userContextData = this.userService.getUserContextData();
    }

    if (this.needsSidenav) {
      this.sidenavActionDefs = getSidenavActionDefs(state);
    }

    this.rightSidenavEnabled = getRightSidenavEnabled(state);
    this.activeArea = getActiveArea(state);
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.media
      .asObservable()
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((mediaChanges: MediaChange[]) => {
        const mediaChange = mediaChanges[0];
        if (mediaChange.mqAlias === 'xs') {
          this.posTop = 56;
        } else {
          this.posTop = 64;
        }
      });

    this.store.subscribe((state) => this.handleState(state));
  }

  ngAfterViewInit() {
    this.sidenavRightService.sidenav = this.sidenavRight;
  }

  onActivateRoute(activatedComponent) {
    this.currentComponent = activatedComponent;
    this.scrollContainer.nativeElement.scrollTo({ top: 0 });
  }

  logout() {
    if (this.frameHandler?.logoutAction) {
      this.frameHandler.logoutAction(this);
    } else {
      void this.navigateToExternalUrl('/logout/');
    }
  }

  login() {
    if (this.frameHandler?.loginAction) {
      this.frameHandler.loginAction(this);
    } else {
      window.open('/portal/loginapp.html#/login/INIT', '_self');
    }
  }

  gotoBenutzereinstellungen() {
    if (this.frameHandler?.benutzerEinstellungenAction) {
      this.frameHandler.benutzerEinstellungenAction(this);
    } else {
      void this.navigateToExternalUrl('/portal/partner.html#/profil');
    }
  }

  navigateToRoute(route: string[]) {
    this.router.navigate(route).then(() => {
      void this.checkClosing();
    });
  }

  async canDeactivateCurrentComponent(): Promise<boolean> {
    if (this.currentComponent?.canDeactivate) {
      const canDeactivate: boolean = await this.currentComponent.canDeactivate();
      if (!canDeactivate) {
        return false;
      }
    }
    return true;
  }

  async navigateToExternalUrl(url: string, target = '_self') {
    const canDeactivate = await this.canDeactivateCurrentComponent();
    if (!canDeactivate) {
      return;
    }
    const windowRef = window.open(url, target);

    if (target === '_blank') {
      window.addEventListener('message', (event) => {
        if (event.data === 'closeWindow' && windowRef) {
          windowRef.close();
        }
      });
    }
  }

  async navigateToExternalRoute(route: ExternalRoute) {
    await this.navigateToExternalUrl(route.url, route.target);
  }

  async fireAction(action: () => void) {
    action();
    await this.checkClosing();
  }

  async checkClosing() {
    if (this.needsSidenav) {
      await this.sidenav.close();
    }
  }

  get frameTitle(): string {
    return this.activeArea?.title;
  }

  navigateHome(event: MouseEvent) {
    event.preventDefault();
    if (this.frameHandler?.navigateHomeAction) {
      this.frameHandler.navigateHomeAction(this);
    } else {
      void this.navigateToExternalUrl(this.logoHref);
    }
  }

  get contentTopMargin(): number {
    if (this.noFrame) {
      return 0;
    }
    return this.withTabs ? this.posTop + 48 : this.posTop;
  }

  get contentHeight(): SafeStyle {
    if (this.noFrame) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustStyle('calc(100% - ' + this.contentTopMargin + 'px)');
  }

  get currentTabSubnavs(): MngActionDef[] {
    for (const actiondef of this.sidenavActionDefs) {
      if ((this.isActiveRoute(actiondef) || this.isAnyActive(actiondef.menu)) && actiondef.menu) {
        return actiondef.menu.filter((ad) => !ad.hiddenInSomePlace);
      }
    }
  }

  isActive(actionDef: MngActionDef): boolean {
    const currentPath = this.location.path();
    const pathWithoutParams = removeParams(currentPath).substr(1);

    if (actionDef.targetComparePaths) {
      return actionDef.targetComparePaths.some((comparPath) => comparPath.startsWith(pathWithoutParams));
    }

    const target = actionDef.target && actionDef.target.length > 0 ? actionDef.target[0] : '';
    return target.startsWith(pathWithoutParams);
  }

  isAnyActive(actionDefs: MngActionDef[]): boolean {
    if (!actionDefs) {
      return false;
    }
    for (const def of actionDefs) {
      if (this.isActiveRoute(def)) {
        return true;
      }
    }
    return false;
  }

  isActiveRoute(actionDef: MngActionDef): boolean {
    const currentPath = this.location.path();
    const pathWithoutParams = removeParams(currentPath);

    return !!actionDef.target && pathWithoutParams === actionDef.target[0];
  }

  executeAction(actionDef: MngActionDef) {
    if (actionDef.target) {
      this.navigateToRoute(actionDef.target);
    }

    if (actionDef.action) {
      this.fireAction(actionDef.action);
    }

    if (actionDef.externalUrl) {
      this.navigateToExternalRoute({ url: actionDef.externalUrl, target: actionDef.externalUrlTarget });
    }
  }

  get withTabs(): boolean {
    return this.withNavTabs && !!this.currentTabSubnavs && this.currentTabSubnavs.length > 0;
  }
}
